import type { InventoryApi, InventoryTarget } from '#contract'
import type { ScreenUi } from './screen-context.ts'
import { sameTarget, targetOf } from './screen-context.ts'

/** Moving this far before letting go makes a press a drag. */
const DRAG_PX = 6
/** A touch held this long is a right click: take or put one. */
const LONG_PRESS_MS = 400

/**
 * One press on a slot or the bin, followed to its release with window listeners, so it
 * needs no component state.
 *
 * - Mouse and pen: the press clicks at once (left: primary, right: secondary, Shift:
 *   quick). Letting go over another slot after moving sends a primary click there, so a
 *   drag moves the stack.
 * - Touch: a tap is primary, a long press secondary, and the Quick move toggle makes them
 *   quick. Dragging takes the stack and puts it where the finger lifts.
 */
export function pressSlot(
  event: PointerEvent,
  target: InventoryTarget,
  inventory: InventoryApi,
  ui: ScreenUi,
): void {
  const touch = event.pointerType === 'touch'
  if (!touch && event.button !== 0 && event.button !== 2) return
  const start = { x: event.clientX, y: event.clientY }
  ui.movePointer(start)
  let dragging = false
  let longPressed = false
  let timer: ReturnType<typeof setTimeout> | undefined

  if (touch) {
    timer = setTimeout(() => {
      longPressed = true
      inventory.click(target, 'secondary', ui.quick.value)
    }, LONG_PRESS_MS)
  } else {
    inventory.click(target, event.button === 2 ? 'secondary' : 'primary', event.shiftKey)
  }

  const controller = new AbortController()
  const mine = (e: PointerEvent): boolean => e.pointerId === event.pointerId
  const end = (): void => {
    clearTimeout(timer)
    controller.abort()
  }
  window.addEventListener(
    'pointermove',
    (e) => {
      if (!mine(e) || dragging || longPressed) return
      if (Math.hypot(e.clientX - start.x, e.clientY - start.y) < DRAG_PX) return
      dragging = true
      clearTimeout(timer)
      // A finger has taken nothing yet: the drag takes the stack.
      if (touch) inventory.click(target, 'primary', false)
    },
    { signal: controller.signal },
  )
  window.addEventListener(
    'pointerup',
    (e) => {
      if (!mine(e)) return
      end()
      if (dragging) {
        const over = targetOf(document.elementFromPoint(e.clientX, e.clientY))
        if (over !== null && !sameTarget(over, target)) inventory.click(over, 'primary', false)
      } else if (touch && !longPressed) {
        inventory.click(target, 'primary', ui.quick.value)
      }
    },
    { signal: controller.signal },
  )
  window.addEventListener(
    'pointercancel',
    (e) => {
      if (mine(e)) end()
    },
    { signal: controller.signal },
  )
}
